/**
 * video-telemetry.js — multi-point GPS TRACK extraction for video-location.
 *
 * Where video-meta.js reads a single embedded GPS point (iPhone/Android
 * ©xyz/ISO6709/loci), THIS module pulls the full per-frame route that action
 * cams, drones and dashcams record — the thing no online tool surfaces well:
 *
 *   • GoPro GPMF   — the `gpmd` timed-metadata track (GPS5/GPS9 + SCAL + GPSU).
 *   • DJI / subtitle — an embedded subtitle track whose cues carry
 *     `GPS(lon,lat,alt)` (old) or `[latitude: x] [longitude: y]` (new).
 *   • Novatek freeGPS — the `freeGPS` blocks Viofo & most cheap dashcams
 *     interleave into mdat.
 *   • Sidecar files — a .SRT / .GPX / .KML / .NMEA the user uploads next to
 *     (or instead of) the video (DJI sidecar, BlackVue .gps, Strava, …).
 *
 * Design:
 *   - Never loads the whole file. It parses `moov` (bounded), reads each
 *     telemetry track's sample table (stsc/stco/stsz), then reads ONLY the
 *     sample byte ranges — a few MB even for a 1-hour 1-GB clip.
 *   - Pure functions (parseTracks, GPMF/subtitle/nmea/gpx/kml parsers) are
 *     exported for the Node unit test; nothing here throws — a bad file
 *     yields `{ source:null, track:[] }`.
 *
 * Usage:
 *   const t = await window.VideoTelemetry.parse(file);
 *   // → { source, track:[{lat,lon,alt,speed,t,vt}], stats }
 *   const t2 = window.VideoTelemetry.parseSidecar(text, filename);
 */
(function () {
'use strict';

var MAX_MOOV_BYTES = 64 * 1024 * 1024;
var MAX_TELEMETRY_BYTES = 96 * 1024 * 1024; // cap on sample bytes actually read
var NOVATEK_SCAN_CAP = 512 * 1024 * 1024;   // cap the freeGPS mdat scan

/* ───────────────── ISO-BMFF primitives ───────────────── */

function fourcc(dv, off) {
    return String.fromCharCode(dv.getUint8(off), dv.getUint8(off + 1),
                               dv.getUint8(off + 2), dv.getUint8(off + 3));
}

// Iterate boxes in [start,end): cb(type, payloadStart, payloadEnd, boxStart, headerSize).
function eachBox(dv, start, end, cb) {
    var off = start, guard = 0;
    while (off + 8 <= end && guard++ < 2000000) {
        var size = dv.getUint32(off);
        var type = fourcc(dv, off + 4);
        var headerSize = 8;
        if (size === 1) {
            size = dv.getUint32(off + 8) * 4294967296 + dv.getUint32(off + 12);
            headerSize = 16;
        } else if (size === 0) {
            size = end - off;
        }
        if (size < headerSize || off + size > end) break;
        if (cb(type, off + headerSize, off + size, off, headerSize) === false) return;
        off += size;
    }
}

function findBox(dv, start, end, wanted) {
    var found = null;
    eachBox(dv, start, end, function (type, ps, pe) {
        if (type === wanted && !found) { found = { ps: ps, pe: pe }; return false; }
    });
    return found;
}

/* ───────────────── sample-table reader ─────────────────
   Returns, for every track, its handler/format and the ordered list of
   absolute (offset,size) sample ranges. moovFileOffset lets chunk offsets
   (which are absolute in the file) stay absolute. */

function readFullBoxU32Array(dv, ps, pe, headerBytes) {
    // [version+flags(4)][count(4)][count × u32]
    var count = dv.getUint32(ps + 4), arr = [], o = ps + 8 + (headerBytes || 0);
    for (var i = 0; i < count && o + 4 <= pe; i++, o += 4) arr.push(dv.getUint32(o));
    return arr;
}

function parseTracks(moovBuf) {
    var dv = new DataView(moovBuf);
    var tracks = [];
    // moov is the whole buffer here (caller sliced [moovStart, moovEnd]).
    var moov = findBox(dv, 0, moovBuf.byteLength, 'moov');
    if (!moov) return tracks;

    eachBox(dv, moov.ps, moov.pe, function (t, cs, ce) {
        if (t !== 'trak') return;
        var handler = '', format = '', sampleTable = null;

        var mdia = findBox(dv, cs, ce, 'mdia');
        if (!mdia) return;
        var hdlr = findBox(dv, mdia.ps, mdia.pe, 'hdlr');
        if (hdlr) handler = fourcc(dv, hdlr.ps + 8); // version+flags(4)+predefined(4)→handler_type
        var minf = findBox(dv, mdia.ps, mdia.pe, 'minf');
        if (!minf) return;
        var stbl = findBox(dv, minf.ps, minf.pe, 'stbl');
        if (!stbl) return;

        var stsd = findBox(dv, stbl.ps, stbl.pe, 'stsd');
        if (stsd) format = fourcc(dv, stsd.ps + 8 + 4); // v+f(4)+count(4)+entry_size(4)→fourcc

        var stsc = findBox(dv, stbl.ps, stbl.pe, 'stsc');
        var stco = findBox(dv, stbl.ps, stbl.pe, 'stco');
        var co64 = findBox(dv, stbl.ps, stbl.pe, 'co64');
        var stsz = findBox(dv, stbl.ps, stbl.pe, 'stsz');
        var stts = findBox(dv, stbl.ps, stbl.pe, 'stts');

        var chunkOffsets = [];
        if (stco) chunkOffsets = readFullBoxU32Array(dv, stco.ps, stco.pe);
        else if (co64) {
            var cnt = dv.getUint32(co64.ps + 4), o = co64.ps + 8;
            for (var i = 0; i < cnt && o + 8 <= co64.pe; i++, o += 8)
                chunkOffsets.push(dv.getUint32(o) * 4294967296 + dv.getUint32(o + 4));
        }

        var sizes = [];
        if (stsz) {
            var sampleSize = dv.getUint32(stsz.ps + 4);
            var sampleCount = dv.getUint32(stsz.ps + 8);
            if (sampleSize !== 0) { for (var s = 0; s < sampleCount; s++) sizes.push(sampleSize); }
            else { var so = stsz.ps + 12; for (var s2 = 0; s2 < sampleCount && so + 4 <= stsz.pe; s2++, so += 4) sizes.push(dv.getUint32(so)); }
        }

        // stsc runs → samples-per-chunk expanded across chunks
        var stscEntries = [];
        if (stsc) {
            var ec = dv.getUint32(stsc.ps + 4), eo = stsc.ps + 8;
            for (var e = 0; e < ec && eo + 12 <= stsc.pe; e++, eo += 12)
                stscEntries.push({ firstChunk: dv.getUint32(eo), spc: dv.getUint32(eo + 4) });
        }

        // durations (stts) → for video-time mapping
        var deltas = [];
        if (stts) {
            var tc = dv.getUint32(stts.ps + 4), to = stts.ps + 8;
            for (var d = 0; d < tc && to + 8 <= stts.pe; d++, to += 8) {
                var n = dv.getUint32(to), dl = dv.getUint32(to + 4);
                for (var k = 0; k < n; k++) deltas.push(dl);
            }
        }

        if (chunkOffsets.length && sizes.length && stscEntries.length) {
            sampleTable = buildSampleTable(chunkOffsets, sizes, stscEntries);
        }
        tracks.push({ handler: handler, format: format, sampleTable: sampleTable, deltas: deltas });
    });
    return tracks;
}

function buildSampleTable(chunkOffsets, sizes, stscEntries) {
    var numChunks = chunkOffsets.length;
    var spc = new Array(numChunks).fill(0);
    for (var i = 0; i < stscEntries.length; i++) {
        var first = stscEntries[i].firstChunk;                    // 1-based
        var last = (i + 1 < stscEntries.length) ? stscEntries[i + 1].firstChunk - 1 : numChunks;
        for (var c = first; c <= last && c <= numChunks; c++) spc[c - 1] = stscEntries[i].spc;
    }
    var out = [], sIdx = 0;
    for (var ch = 0; ch < numChunks; ch++) {
        var off = chunkOffsets[ch];
        for (var s = 0; s < spc[ch] && sIdx < sizes.length; s++) {
            out.push({ offset: off, size: sizes[sIdx] });
            off += sizes[sIdx];
            sIdx++;
        }
    }
    return out;
}

/* ───────────────── range reading (browser + node) ───────────────── */

function makeReader(file) {
    // Browser File/Blob → {size, read(start,end):Promise<ArrayBuffer>}
    return { size: file.size, read: function (a, b) { return file.slice(a, b).arrayBuffer(); } };
}

// Merge sample ranges into read-groups (gap < 256 KB) to cut the number of
// slice() calls, then read each group once and cut the samples back out.
async function readSamples(reader, table) {
    if (!table || !table.length) return new Uint8Array(0);
    var sorted = table.slice().sort(function (a, b) { return a.offset - b.offset; });
    var groups = [], cur = { start: sorted[0].offset, end: sorted[0].offset + sorted[0].size };
    for (var i = 1; i < sorted.length; i++) {
        var s = sorted[i];
        if (s.offset - cur.end < 262144) cur.end = Math.max(cur.end, s.offset + s.size);
        else { groups.push(cur); cur = { start: s.offset, end: s.offset + s.size }; }
    }
    groups.push(cur);
    var total = groups.reduce(function (a, g) { return a + (g.end - g.start); }, 0);
    if (total > MAX_TELEMETRY_BYTES) return new Uint8Array(0);

    var bufByStart = {};
    for (var g = 0; g < groups.length; g++) {
        var grp = groups[g];
        if (grp.end > reader.size) grp.end = reader.size;
        bufByStart[grp.start] = new Uint8Array(await reader.read(grp.start, grp.end));
    }
    // Concatenate samples in ORIGINAL order (table order = time order).
    var outLen = table.reduce(function (a, s) { return a + s.size; }, 0);
    var out = new Uint8Array(outLen), p = 0;
    for (var j = 0; j < table.length; j++) {
        var smp = table[j];
        // find owning group
        var grpStart = -1;
        for (var gg = 0; gg < groups.length; gg++) { if (smp.offset >= groups[gg].start && smp.offset < groups[gg].end) { grpStart = groups[gg].start; break; } }
        if (grpStart < 0) continue;
        var buf = bufByStart[grpStart];
        var local = smp.offset - grpStart;
        out.set(buf.subarray(local, local + smp.size), p);
        p += smp.size;
    }
    return out.subarray(0, p);
}

/* ───────────────── GPMF (GoPro) parser ───────────────── */

function parseGPMF(bytes) {
    var dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    var track = [];
    var scal = null, gpsu = null, gpsf = null, gpsp = null;

    function readInt(o, comp) { return dv.getInt32(o + comp * 4); }

    function walk(start, end) {
        var o = start;
        while (o + 8 <= end) {
            var key = String.fromCharCode(bytes[o], bytes[o + 1], bytes[o + 2], bytes[o + 3]);
            var type = bytes[o + 4];
            var structSize = bytes[o + 5];
            var repeat = (bytes[o + 6] << 8) | bytes[o + 7];
            var payloadLen = structSize * repeat;
            var ps = o + 8;
            var padded = payloadLen + ((4 - (payloadLen % 4)) % 4);

            if (type === 0x00) {
                walk(ps, ps + payloadLen);
            } else if (key === 'SCAL') {
                scal = [];
                var isShort = (bytes[o + 4] === 0x73 || bytes[o + 4] === 0x53); // 's'/'S'
                for (var i = 0; i < repeat; i++) {
                    if (structSize === 2) scal.push(dv.getInt16(ps + i * 2));
                    else scal.push(dv.getInt32(ps + i * structSize));
                }
            } else if (key === 'GPSU') {
                gpsu = decodeAscii(bytes, ps, payloadLen).trim();
            } else if (key === 'GPSF') {
                gpsf = repeat ? dv.getUint32(ps) : null;
            } else if (key === 'GPSP') {
                gpsp = repeat ? dv.getUint16(ps) : null;
            } else if (key === 'GPS5') {
                var comps = structSize / 4;
                for (var s = 0; s < repeat; s++) {
                    var base = ps + s * structSize;
                    var lat = readInt(base, 0) / scl(0), lon = readInt(base, 1) / scl(1);
                    var alt = comps > 2 ? readInt(base, 2) / scl(2) : null;
                    var spd = comps > 3 ? readInt(base, 3) / scl(3) : null;
                    push(lat, lon, alt, spd);
                }
            } else if (key === 'GPS9') {
                // lat,lon,alt,spd2d,spd3d,days,secs,dop,fix — first 4 scaled by SCAL
                for (var s9 = 0; s9 < repeat; s9++) {
                    var b9 = ps + s9 * structSize;
                    push(dv.getInt32(b9) / scl(0), dv.getInt32(b9 + 4) / scl(1),
                         dv.getInt32(b9 + 8) / scl(2), dv.getInt32(b9 + 12) / scl(3));
                }
            }
            o = ps + padded;
        }
    }
    function scl(i) { return (scal && scal.length) ? (scal[i] != null ? scal[i] : scal[0]) : 1; }
    function push(lat, lon, alt, spd) {
        if (!isFinite(lat) || !isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return;
        if (lat === 0 && lon === 0) return;
        if (gpsf != null && gpsf === 0) return; // no fix
        track.push({ lat: lat, lon: lon, alt: (alt != null && isFinite(alt)) ? alt : null,
                     speed: (spd != null && isFinite(spd)) ? spd : null, t: parseGPSU(gpsu) });
    }
    walk(0, bytes.length);
    return track;
}

function parseGPSU(s) {
    // "yyMMddHHmmss.sss" (GoPro) → Date (UTC)
    if (!s) return null;
    var m = s.match(/^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2}(?:\.\d+)?)/);
    if (!m) return null;
    var yr = 2000 + parseInt(m[1], 10);
    return new Date(Date.UTC(yr, +m[2] - 1, +m[3], +m[4], +m[5], parseFloat(m[6])));
}

function decodeAscii(bytes, off, len) {
    var s = '';
    for (var i = 0; i < len; i++) { var c = bytes[off + i]; if (c === 0) break; s += String.fromCharCode(c); }
    return s;
}

/* ───────────────── subtitle-track text parser (DJI / generic SRT) ─────────────────
   Subtitle samples: tx3g/text have a 2-byte length prefix + UTF-8 payload;
   others are raw text. We decode loosely and run the shared SRT/DJI parser. */

function decodeSubtitleSamples(bytes, format) {
    var td = (typeof TextDecoder !== 'undefined') ? new TextDecoder('utf-8', { fatal: false }) : null;
    // Strip tx3g 2-byte length prefixes if present; otherwise decode whole blob.
    // Simplest robust approach: decode the whole blob and let the regex find cues.
    var text = td ? td.decode(bytes) : decodeAscii(bytes, 0, bytes.length);
    return text.replace(/\u0000/g, ' ');
}

/* Shared DJI / SRT text → points. Handles both eras + labelled variants. */
function parseSrtText(text) {
    var track = [];
    function addPt(lat, lon, alt) {
        if (!isFinite(lat) || !isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return;
        if (lat === 0 && lon === 0) return;
        track.push({ lat: lat, lon: lon, alt: (alt != null && isFinite(alt)) ? alt : null, speed: null, t: null });
    }
    var m;
    // 1) Labelled (unambiguous): [latitude: x] [longitude: y]  (either order)
    var re1 = /latitude\s*:?\s*(-?\d+\.\d+)[\s\S]{0,60}?longitude\s*:?\s*(-?\d+\.\d+)/gi;
    while ((m = re1.exec(text))) addPt(+m[1], +m[2]);
    if (!track.length) {
        var re1b = /longitude\s*:?\s*(-?\d+\.\d+)[\s\S]{0,60}?latitude\s*:?\s*(-?\d+\.\d+)/gi;
        while ((m = re1b.exec(text))) addPt(+m[2], +m[1]);
    }
    if (track.length) return track;

    // 2) Bare GPS(a,b[,alt…]) — order differs across DJI eras. Disambiguate:
    //    |a|>90 ⇒ a is longitude (old Mavic: lon,lat); |b|>90 ⇒ b is longitude
    //    (newer: lat,lon); if both ≤90, a HOME(lon,lat) tag ⇒ old (lon,lat),
    //    else assume newer (lat,lon). Tolerate a trailing "M"/other tokens.
    var lonFirstHint = /HOME\s*\(/i.test(text);
    var re2 = /GPS\s*\(\s*(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)\s*(?:,\s*(-?\d+(?:\.\d+)?)[^)]*)?\)/gi;
    while ((m = re2.exec(text))) {
        var a = +m[1], b = +m[2], alt = m[3] != null ? +m[3] : null, lat, lon;
        if (Math.abs(a) > 90 && Math.abs(b) <= 90) { lon = a; lat = b; }
        else if (Math.abs(b) > 90 && Math.abs(a) <= 90) { lat = a; lon = b; }
        else if (lonFirstHint) { lon = a; lat = b; }
        else { lat = a; lon = b; }
        addPt(lat, lon, alt);
    }
    return track;
}

/* ───────────────── Novatek freeGPS (Viofo & clones) ─────────────────
   Scans for the ASCII marker "freeGPS " and decodes the common little-endian
   block: hemispheres (N/S,E/W) + lat/lon as DDDmm.mmmm floats. Covers the
   Novatek layout nvtk_mp42gpx documents; unknown variants are skipped. */

function parseNovatek(bytes) {
    var track = [];
    var marker = [0x66, 0x72, 0x65, 0x65, 0x47, 0x50, 0x53]; // "freeGPS"
    var dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    for (var i = 0; i + 88 < bytes.length; i++) {
        if (bytes[i] !== marker[0]) continue;
        var hit = true;
        for (var k = 1; k < 7; k++) if (bytes[i + k] !== marker[k]) { hit = false; break; }
        if (!hit) continue;
        // Common layout: at +48 active('A'), +49 lat-hemi, +50 lon-hemi, then
        // 32-bit LE floats: lat(+53), lon(+57), speed(+61), bearing(+65).
        var base = i;
        var pt = tryNovatekBlock(dv, bytes, base);
        if (pt) track.push(pt);
    }
    return track;
}

function tryNovatekBlock(dv, bytes, base) {
    // Try a couple of known offsets for the hemisphere+float layout.
    var candidates = [48, 16];
    for (var ci = 0; ci < candidates.length; ci++) {
        var o = base + candidates[ci];
        if (o + 24 > bytes.length) continue;
        var active = bytes[o];
        var latH = bytes[o + 1], lonH = bytes[o + 2];
        if (active !== 0x41 && active !== 0x56) continue; // 'A' active / 'V' void
        if (latH !== 0x4E && latH !== 0x53) continue;     // N/S
        if (lonH !== 0x45 && lonH !== 0x57) continue;     // E/W
        var latRaw = dv.getFloat32(o + 5, true);
        var lonRaw = dv.getFloat32(o + 9, true);
        var spd = dv.getFloat32(o + 13, true);
        var lat = ddmmToDeg(latRaw), lon = ddmmToDeg(lonRaw);
        if (lat == null || lon == null) continue;
        if (latH === 0x53) lat = -lat;
        if (lonH === 0x57) lon = -lon;
        if (Math.abs(lat) > 90 || Math.abs(lon) > 180 || (lat === 0 && lon === 0)) continue;
        return { lat: lat, lon: lon, alt: null, speed: isFinite(spd) ? spd * 1.852 / 3.6 : null, t: null };
    }
    return null;
}

function ddmmToDeg(v) {
    if (!isFinite(v) || v < 0 || v > 18000) return null;
    var deg = Math.floor(v / 100);
    var min = v - deg * 100;
    if (min >= 60) return null;
    return deg + min / 60;
}

/* ───────────────── sidecar parsers (.srt/.gpx/.kml/.nmea) ───────────────── */

function parseGPX(text) {
    var track = [], re = /<trkpt[^>]*\blat="(-?\d+\.?\d*)"[^>]*\blon="(-?\d+\.?\d*)"|<trkpt[^>]*\blon="(-?\d+\.?\d*)"[^>]*\blat="(-?\d+\.?\d*)"/gi, m;
    // also capture <rtept>/<wpt>
    var re2 = /<(?:trkpt|rtept|wpt)\s+[^>]*?lat="(-?\d+\.?\d*)"[^>]*?lon="(-?\d+\.?\d*)"[^>]*?>([\s\S]*?)<\/(?:trkpt|rtept|wpt)>/gi;
    while ((m = re2.exec(text))) {
        var lat = +m[1], lon = +m[2], seg = m[3] || '';
        var ele = seg.match(/<ele>\s*(-?\d+\.?\d*)/i), tm = seg.match(/<time>\s*([^<]+)/i);
        if (valid(lat, lon)) track.push({ lat: lat, lon: lon, alt: ele ? +ele[1] : null, speed: null, t: tm ? new Date(tm[1]) : null });
    }
    if (!track.length) while ((m = re.exec(text))) {
        var la = m[1] != null ? +m[1] : +m[4], lo = m[2] != null ? +m[2] : +m[3];
        if (valid(la, lo)) track.push({ lat: la, lon: lo, alt: null, speed: null, t: null });
    }
    return track;
}

function parseKML(text) {
    var track = [];
    var blocks = text.match(/<coordinates>([\s\S]*?)<\/coordinates>/gi) || [];
    blocks.forEach(function (b) {
        var inner = b.replace(/<\/?coordinates>/gi, '').trim();
        inner.split(/\s+/).forEach(function (tuple) {
            var p = tuple.split(',');
            if (p.length >= 2) {
                var lon = +p[0], lat = +p[1], alt = p[2] != null ? +p[2] : null;
                if (valid(lat, lon)) track.push({ lat: lat, lon: lon, alt: (alt != null && isFinite(alt)) ? alt : null, speed: null, t: null });
            }
        });
    });
    return track;
}

function parseNMEA(text) {
    var track = [], lines = text.split(/\r?\n/);
    lines.forEach(function (ln) {
        var m = ln.match(/\$G[NPLA]?(GGA|RMC),([^*]*)/);
        if (!m) return;
        var f = m[2].split(',');
        var lat, lon, latH, lonH, spd = null;
        if (m[1] === 'GGA') { lat = f[1]; latH = f[2]; lon = f[3]; lonH = f[4]; }
        else { lat = f[2]; latH = f[3]; lon = f[4]; lonH = f[5]; spd = f[6] ? +f[6] * 1852 / 3600 : null; }
        var la = nmeaDeg(lat, latH), lo = nmeaDeg(lon, lonH);
        if (la != null && lo != null && valid(la, lo)) track.push({ lat: la, lon: lo, alt: null, speed: spd, t: null });
    });
    return track;
}

function nmeaDeg(v, hemi) {
    if (!v) return null;
    var f = parseFloat(v); if (!isFinite(f)) return null;
    var deg = Math.floor(f / 100), min = f - deg * 100, d = deg + min / 60;
    if (hemi === 'S' || hemi === 'W') d = -d;
    return d;
}

function valid(lat, lon) { return isFinite(lat) && isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && !(lat === 0 && lon === 0); }

/* ───────────────── stats + video-time mapping ───────────────── */

function haversine(a, b) {
    var R = 6371000, toRad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * toRad, dLon = (b.lon - a.lon) * toRad;
    var la1 = a.lat * toRad, la2 = b.lat * toRad;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function finalize(track, source, duration) {
    // thin obvious dupes (consecutive identical coords)
    var clean = [];
    for (var i = 0; i < track.length; i++) {
        var p = track[i];
        if (clean.length && clean[clean.length - 1].lat === p.lat && clean[clean.length - 1].lon === p.lon) continue;
        clean.push(p);
    }
    var n = clean.length;
    // assign video-time (seconds) evenly if not present
    if (duration && n > 1) for (var j = 0; j < n; j++) if (clean[j].vt == null) clean[j].vt = duration * j / (n - 1);

    var dist = 0, maxSpeed = 0, minAlt = Infinity, maxAlt = -Infinity, haveAlt = false, haveSpeed = false;
    for (var k = 0; k < n; k++) {
        if (k > 0) dist += haversine(clean[k - 1], clean[k]);
        if (clean[k].speed != null) { haveSpeed = true; if (clean[k].speed > maxSpeed) maxSpeed = clean[k].speed; }
        if (clean[k].alt != null) { haveAlt = true; minAlt = Math.min(minAlt, clean[k].alt); maxAlt = Math.max(maxAlt, clean[k].alt); }
    }
    var lats = clean.map(function (p) { return p.lat; }), lons = clean.map(function (p) { return p.lon; });
    var times = clean.map(function (p) { return p.t; }).filter(Boolean);
    return {
        source: source,
        track: clean,
        stats: {
            points: n,
            distanceMeters: dist,
            maxSpeed: haveSpeed ? maxSpeed : null,
            minAlt: haveAlt ? minAlt : null,
            maxAlt: haveAlt ? maxAlt : null,
            bbox: n ? { minLat: Math.min.apply(null, lats), maxLat: Math.max.apply(null, lats), minLon: Math.min.apply(null, lons), maxLon: Math.max.apply(null, lons) } : null,
            start: clean[0] || null,
            end: clean[n - 1] || null,
            startTime: times.length ? times[0] : null,
            endTime: times.length ? times[times.length - 1] : null
        }
    };
}

/* ───────────────── locate moov (header-only walk) ───────────────── */

async function locateMoov(reader) {
    var offset = 0, size = reader.size, guard = 0;
    while (offset + 16 <= size && guard++ < 100000) {
        var hdr = new DataView(await reader.read(offset, offset + 16));
        var boxSize = hdr.getUint32(0);
        var type = String.fromCharCode(hdr.getUint8(4), hdr.getUint8(5), hdr.getUint8(6), hdr.getUint8(7));
        var headerSize = 8;
        if (boxSize === 1) { boxSize = hdr.getUint32(8) * 4294967296 + hdr.getUint32(12); headerSize = 16; }
        else if (boxSize === 0) boxSize = size - offset;
        if (boxSize < headerSize) break;
        if (type === 'moov') return { start: offset, end: Math.min(offset + boxSize, offset + MAX_MOOV_BYTES, size) };
        offset += boxSize;
    }
    return null;
}

/* ───────────────── public API ───────────────── */

var TELEM_HANDLERS = { meta: 1, sbtl: 1, text: 1, subt: 1, tx3g: 1, gps: 1 };

async function parse(file) {
    var empty = { source: null, track: [], stats: { points: 0 } };
    if (!file || typeof file.slice !== 'function') return empty;
    var reader = makeReader(file);
    var duration = 0;
    try { if (window.__Mp4BoxProbe) { var p = await window.__Mp4BoxProbe(file); duration = p.duration || 0; } } catch (e) {}

    var moovLoc = null;
    try { moovLoc = await locateMoov(reader); } catch (e) {}
    if (moovLoc) {
        try {
            var moovBuf = await reader.read(moovLoc.start, moovLoc.end);
            var tracks = parseTracks(moovBuf);

            // 1) GoPro GPMF (gpmd format)
            var gpmd = tracks.filter(function (t) { return t.format === 'gpmd' && t.sampleTable; })[0];
            if (gpmd) {
                var g = await readSamples(reader, gpmd.sampleTable);
                var pts = parseGPMF(g);
                if (pts.length) return finalize(pts, 'gopro', duration);
            }

            // 2) subtitle track (DJI embedded / generic SRT-in-mp4)
            var sub = tracks.filter(function (t) {
                return t.sampleTable && (t.handler === 'sbtl' || t.handler === 'text' || t.handler === 'subt' ||
                       t.format === 'tx3g' || t.format === 'text' || t.format === 'sbtl');
            })[0];
            if (sub) {
                var sbytes = await readSamples(reader, sub.sampleTable);
                var stext = decodeSubtitleSamples(sbytes, sub.format);
                var spts = parseSrtText(stext);
                if (spts.length) return finalize(spts, 'subtitle', duration);
            }
        } catch (e) {}
    }

    // 3) Novatek freeGPS scan (bounded) — dashcams that don't use a track
    try {
        var scanEnd = Math.min(reader.size, NOVATEK_SCAN_CAP);
        // scan in 16 MB windows with small overlap for the 88-byte block
        var CHUNK = 16 * 1024 * 1024, overlap = 128, novaPts = [];
        for (var so = 0; so < scanEnd; so += CHUNK - overlap) {
            var se = Math.min(so + CHUNK, scanEnd);
            var wnd = new Uint8Array(await reader.read(so, se));
            var found = parseNovatek(wnd);
            if (found.length) novaPts = novaPts.concat(found);
            if (novaPts.length > 200000) break;
        }
        if (novaPts.length) return finalize(novaPts, 'novatek', duration);
    } catch (e) {}

    return empty;
}

function parseSidecar(text, filename) {
    var ext = String(filename || '').split('.').pop().toLowerCase();
    var pts = [];
    if (ext === 'gpx' || /<gpx[\s>]/i.test(text)) pts = parseGPX(text);
    else if (ext === 'kml' || /<kml[\s>]/i.test(text)) pts = parseKML(text);
    else if (ext === 'srt' || /-->/.test(text)) pts = parseSrtText(text);
    else if (ext === 'nmea' || ext === 'gps' || /\$G[NPLA]?(GGA|RMC)/.test(text)) pts = parseNMEA(text);
    else pts = parseSrtText(text) || [];
    var src = ext === 'gpx' ? 'gpx' : ext === 'kml' ? 'kml' : ext === 'nmea' || ext === 'gps' ? 'nmea' : 'srt';
    return finalize(pts, pts.length ? ('sidecar-' + src) : null, 0);
}

var VideoTelemetry = {
    parse: parse,
    parseSidecar: parseSidecar,
    // exposed for the node unit test
    _parseTracks: parseTracks,
    _parseGPMF: parseGPMF,
    _parseSrtText: parseSrtText,
    _parseNovatek: parseNovatek,
    _parseGPX: parseGPX,
    _parseKML: parseKML,
    _parseNMEA: parseNMEA,
    _finalize: finalize,
    _buildSampleTable: buildSampleTable,
    _makeReaderFromBuffer: function (u8) { return { size: u8.length, read: function (a, b) { return Promise.resolve(u8.buffer.slice(u8.byteOffset + a, u8.byteOffset + b)); } }; },
    _locateMoov: locateMoov,
    _readSamples: readSamples
};

if (typeof window !== 'undefined') window.VideoTelemetry = VideoTelemetry;
if (typeof module !== 'undefined' && module.exports) module.exports = VideoTelemetry;

})();
